import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import Icon from '../../components/AppIcon';
import CampaignCreationWizard from './components/CampaignCreationWizard';
import api from '../../api/axios';

const statusColors = {
  active: 'bg-green-50 text-green-700 border border-green-200',
  paused: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  completed: 'bg-blue-50 text-blue-700 border border-blue-200',
  draft: 'bg-gray-100 text-gray-600 border border-gray-200',
  scheduled: 'bg-purple-50 text-purple-700 border border-purple-200',
  processing: 'bg-orange-50 text-orange-700 border border-orange-200',
  inactive: 'bg-red-50 text-red-700 border border-red-200',
};

const statusDot = {
  active: 'bg-green-500',
  paused: 'bg-yellow-500',
  completed: 'bg-blue-500',
  draft: 'bg-gray-400',
  scheduled: 'bg-purple-500',
  processing: 'bg-orange-500',
  inactive: 'bg-red-500',
};

const statusLabels = {
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  draft: 'Draft',
  scheduled: 'Scheduled',
  cancelled: 'Cancelled',
  processing: 'Processing',
  inactive: 'Inactive',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletBalance, setWalletBalance] = useState(null);
  const actionRef = useRef(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/v1/campaigns');
      setCampaigns(data?.data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWalletBalance = useCallback(async () => {
    try {
      const { data } = await api.get('/api/v1/wallet/balance');
      setWalletBalance(data?.data?.balance ?? 0);
    } catch (err) {
      console.error('Failed to fetch wallet balance:', err);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchWalletBalance();
  }, [fetchCampaigns, fetchWalletBalance]);

  const hasPausedForBalance = campaigns?.some((c) => c?.pausedForBalance === true);
  const isLowBalance = walletBalance !== null && walletBalance <= 0;

  // Poll for active campaigns
  useEffect(() => {
    const hasActive = campaigns?.some((c) => c?.status === 'active');
    if (!hasActive) return;
    const interval = setInterval(fetchCampaigns, 15000);
    return () => clearInterval(interval);
  }, [campaigns, fetchCampaigns]);

  useEffect(() => {
    const handleClick = (e) => {
      if (actionRef?.current && !actionRef?.current?.contains(e?.target)) setActionMenuOpen(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleAction = async (action, campaign) => {
    setActionMenuOpen(null);
    try {
      if (action === 'Pause') {
        await api.post(`/api/v1/campaigns/${campaign?.id}/pause`);
      } else if (action === 'Resume') {
        await api.post(`/api/v1/campaigns/${campaign?.id}/resume`);
      } else if (action === 'Launch') {
        await api.post(`/api/v1/campaigns/${campaign?.id}/launch`);
      } else if (action === 'Delete') {
        if (!window.confirm('Are you sure you want to delete this campaign?')) return;
        await api.delete(`/api/v1/campaigns/${campaign?.id}`);
      }
      fetchCampaigns();
    } catch (err) {
      console.error(`Failed to ${action} campaign:`, err);
      alert(err?.response?.data?.message || `Failed to ${action.toLowerCase()} campaign`);
    }
  };

  const getActions = (status) => {
    switch (status) {
      case 'active': return ['Pause'];
      case 'paused': return ['Resume', 'Delete'];
      case 'inactive': return ['Delete'];
      case 'draft': return ['Launch', 'Delete'];
      case 'completed': return ['Delete'];
      case 'cancelled': return ['Delete'];
      default: return [];
    }
  };

  const statuses = ['All', 'active', 'inactive', 'paused', 'completed', 'draft'];

  const filtered = campaigns?.filter((c) => {
    const matchSearch = c?.name?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'All' || c?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content min-h-screen bg-background flex flex-col">
        <NavigationBreadcrumb />
        <div className="px-4 md:px-6 py-6 md:py-8 flex-1">
            {/* Page Header */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">Campaigns</h1>
                <p className="text-sm text-muted-foreground">Manage and monitor your AI-powered outreach campaigns</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setWizardOpen(true)}
                  className="px-4 py-2 rounded-lg bg-[#2F55FF] text-white text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2">
                  <Icon name="Plus" size={16} />
                  New Campaign
                </button>
              </div>
            </div>

            {/* Low Balance Ticker */}
            {(hasPausedForBalance || isLowBalance) && (
              <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                <Icon name="AlertTriangle" size={18} className="text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 flex-1">
                  {hasPausedForBalance
                    ? 'Some campaigns have been paused due to insufficient wallet balance. Recharge to resume them automatically.'
                    : 'Your wallet balance is low. Active campaigns may get paused if balance runs out.'}
                </p>
                <button
                  onClick={() => navigate('/settings?tab=billing')}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-[#2F55FF] text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Icon name="Wallet" size={14} />
                  Recharge
                </button>
              </div>
            )}

            {/* Filter Bar */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-4 mb-4 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Campaign..."
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 focus:border-[#2F55FF]"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F55FF]/20 bg-white text-gray-700"
              >
                {statuses?.map((s) => (
                  <option key={s} value={s}>{s === 'All' ? 'All Status' : statusLabels?.[s] || s}</option>
                ))}
              </select>
            </div>

            {/* Table Card */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-gray-50/60">
                      <th className="text-left px-5 py-3 font-medium text-gray-500 whitespace-nowrap">Campaign Name</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Type</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Total Leads</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Called</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Connected</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Failed</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap">Created</th>
                      <th className="px-4 py-3 font-medium text-gray-500 text-center whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="text-center py-16 text-gray-400">
                          <div className="flex items-center justify-center gap-3">
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                            <span>Loading campaigns...</span>
                          </div>
                        </td>
                      </tr>
                    ) : filtered?.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-20 px-6">
                          <div className="flex flex-col items-center justify-center text-center">
                            <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                              <Icon name="Megaphone" size={28} className="text-blue-400" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-800 mb-1">No Campaigns Yet</h3>
                            <p className="text-sm text-gray-500 max-w-xs mb-6">
                              Create your first campaign to start AI-powered voice outreach.
                            </p>
                            <button
                              onClick={() => setWizardOpen(true)}
                              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2F55FF] text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
                            >
                              <Icon name="Plus" size={15} />
                              New Campaign
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filtered?.map((c, idx) => (
                        <tr
                          key={c?.id}
                          className="border-b border-[#F3F4F6] hover:bg-gray-50/60 transition-colors cursor-pointer group"
                          onClick={() => navigate(`/campaigns/${c?.id}`)}
                        >
                          <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap">
                            <span className="group-hover:text-[#2F55FF] transition-colors">{c?.name}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors?.[c?.status] || 'bg-gray-100 text-gray-600'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${statusDot?.[c?.status] || 'bg-gray-400'}`} />
                                {statusLabels?.[c?.status] || c?.status}
                              </span>
                              {c?.pausedForBalance && (
                                <span className="text-[10px] text-red-500 font-medium pl-1">Low Balance</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-gray-700 whitespace-nowrap capitalize">{c?.type?.replace(/_/g, ' ') || '—'}</td>
                          <td className="px-4 py-4 text-right text-gray-700 whitespace-nowrap">{c?.totalLeads?.toLocaleString() || 0}</td>
                          <td className="px-4 py-4 text-right text-gray-700 whitespace-nowrap">{c?.leadsCalled?.toLocaleString() || 0}</td>
                          <td className="px-4 py-4 text-right text-gray-700 whitespace-nowrap">{c?.leadsConnected?.toLocaleString() || 0}</td>
                          <td className="px-4 py-4 text-right text-gray-700 whitespace-nowrap">{c?.leadsFailed?.toLocaleString() || 0}</td>
                          <td className="px-4 py-4 text-gray-500 whitespace-nowrap">{formatDate(c?.createdAt)}</td>
                          <td className="px-4 py-4 text-center whitespace-nowrap" onClick={(e) => e?.stopPropagation()}>
                            <div className="relative inline-block" ref={actionMenuOpen === c?.id ? actionRef : null}>
                              <button
                                onClick={() => setActionMenuOpen(actionMenuOpen === c?.id ? null : c?.id)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                              >
                                <Icon name="MoreVertical" size={16} />
                              </button>
                              {actionMenuOpen === c?.id && (
                                <div className={`absolute right-0 w-36 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-30 py-1 ${idx >= filtered.length - 1 ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                                  {getActions(c?.status)?.map((action) => (
                                    <button
                                      key={action}
                                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${action === 'Delete' ? 'text-red-600' : 'text-gray-700'}`}
                                      onClick={() => handleAction(action, c)}
                                    >
                                      {action}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
      </div>
      {wizardOpen && (
        <CampaignCreationWizard
          onClose={() => { setWizardOpen(false); fetchCampaigns(); }}
        />
      )}
    </>
  );
};

export default Campaigns;
